import React, { useState } from "react";
import { ChevronDown, ChevronRight, Minus, Plus } from "lucide-react";
import { Input } from "../ui/input";
import { cn } from "../../lib/utils";
import { ChipGrid, DeleteConfirm, Field } from "./ProfileParts";
import { BENCHMARK_KINDS, benchmarkFromForm } from "./profileSummaries";
import {
  OTHER_UNITS,
  RESULT_TYPES,
  effortOptions,
  formForSave,
} from "./benchmarkForm";
import { VBT_LIFT_KEYS } from "../../utils/performanceProfile";
import { normaliseExerciseKey } from "../../utils/repEstimator";
import {
  benchmarkAge,
  estimateOneRm,
  normaliseEndurance,
  todayIso,
} from "../../utils/benchmarkNormalise";
import { formatClockInput } from "../../utils/clockInput";
import UnitToggle from "../ui/UnitToggle";
import { useWeightUnit } from "../../contexts/WeightUnitContext";
import { fromDisplayWeight, showWeight, toDisplayWeight } from "../../utils/units";

/**
 * One benchmark's form: pick what you did, enter only those numbers, and
 * see what Khronos makes of them straight away. Rarely used fields fold
 * under "Notes and more".
 */

// The form as a row, for the live estimate. Null while the form is invalid.
const previewRow = (form) => {
  const result = benchmarkFromForm(formForSave(form));
  if (result.error) return null;
  const d = result.data;
  return { ...d, target_value: d.targetValue, target_unit: d.targetUnit, tested_on: d.testedOn };
};

/** What Khronos makes of the entry: a 1RM, a pace or a rate. */
const Estimate = ({ form }) => {
  const row = previewRow(form);
  const oneRm = row && estimateOneRm(row);
  const endurance = row && !oneRm ? normaliseEndurance(row) : null;
  let figure;
  let detail = null;
  if (oneRm?.tested) {
    figure = `${showWeight(oneRm.kg)} tested max`;
    detail = "Counts straight in as your 1RM.";
  } else if (oneRm) {
    figure = `${"≈"} ${showWeight(oneRm.kg)} 1RM`;
    detail = oneRm.rough
      ? row.effort
        ? "Rough: further from your limit, so harder to judge."
        : "Rough until you add reps left in the tank."
      : null;
  } else if (endurance?.kind === "pace") {
    figure = `${endurance.pace} ${endurance.per}`;
    if (endurance.equivalents.length) {
      detail = `About the same as ${endurance.equivalents.map((e) => `${e.distance} in ${e.time}`).join(" or ")}.`;
    }
  } else if (endurance?.kind === "rate") {
    figure = `${"≈"} ${endurance.calPerMin} cal/min`;
  } else if (form.type === "lift" && Number(form.reps) + 0 > 20) {
    detail = "Over 20 reps says more about endurance than strength, so no 1RM from this one.";
  } else if (form.type === "other") {
    detail = "No 1RM or pace from this, but the AI still uses it.";
  } else {
    return null;
  }
  return (
    <div className="grid gap-0.5 border-t border-border pt-2.5" aria-live="polite">
      {figure && (
        <p className="flex items-center gap-2 text-lg font-semibold tabular-nums text-foreground">
          <span className="inline-block h-2 w-2 rounded-full bg-primary" aria-hidden="true" />
          {figure}
        </p>
      )}
      {detail && <p className="text-xs leading-snug text-muted-foreground">{detail}</p>}
    </div>
  );
};

/** A number with − and + either side, still typeable. */
const NumberField = ({ id, label, value, onChange, step, min = 0, unit, placeholder }) => {
  const nudge = (d) => {
    const n = Number(value) || 0;
    const next = Math.max(min, Math.round((n + d) * 100) / 100);
    onChange(String(next));
  };
  return (
    <div className="grid grid-cols-[4.5rem_minmax(0,1fr)] items-center gap-2.5">
      <label htmlFor={id} className="mb-0 text-sm font-medium text-muted-foreground">
        {label}
      </label>
      <div className="grid h-12 grid-cols-[3rem_minmax(0,1fr)_3rem] items-center rounded-xl border border-border bg-background">
        <button
          type="button"
          aria-label={`${label}: less`}
          onClick={() => nudge(-step)}
          className="flex h-full items-center justify-center text-muted-foreground hover:text-foreground"
        >
          <Minus className="h-4 w-4" />
        </button>
        <span className="flex min-w-0 items-baseline justify-center gap-1">
          <input
            id={id}
            type="number"
            inputMode="decimal"
            min={min}
            step="any"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="w-full min-w-0 bg-transparent text-center text-xl font-semibold tabular-nums text-foreground outline-none [appearance:textfield] placeholder:text-muted-foreground/50 [&::-webkit-inner-spin-button]:appearance-none"
          />
          {unit && <span className="shrink-0 text-xs text-muted-foreground">{unit}</span>}
        </span>
        <button
          type="button"
          aria-label={`${label}: more`}
          onClick={() => nudge(step)}
          className="flex h-full items-center justify-center text-muted-foreground hover:text-foreground"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

/** A time as mm:ss. */
const ClockField = ({ id, label, value, onChange }) => (
  <div className="grid grid-cols-[4.5rem_minmax(0,1fr)] items-center gap-2.5">
    <label htmlFor={id} className="mb-0 text-sm font-medium text-muted-foreground">
      {label}
    </label>
    <div className="flex h-12 items-center gap-1 rounded-xl border border-border bg-background px-4">
      <input
        id={id}
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(formatClockInput(e.target.value))}
        placeholder="0:00"
        className="w-full min-w-0 bg-transparent text-xl font-semibold tabular-nums text-foreground outline-none placeholder:text-muted-foreground/50"
      />
      <span className="shrink-0 text-xs text-muted-foreground">mm:ss</span>
    </div>
  </div>
);

// The weight in kg or lb (#191): starts on the Profile unit, and the form
// always holds kg. In lb the typed text is kept as typed, so "22.5" isn't
// rounded away mid-entry.
const WeightField = ({ value, onChange }) => {
  const preferred = useWeightUnit();
  const [unit, setUnit] = useState(preferred);
  const [lbText, setLbText] = useState(() => (value === "" ? "" : String(toDisplayWeight(value, "lb") ?? "")));
  const switchTo = (u) => {
    if (u === "lb") setLbText(value === "" ? "" : String(toDisplayWeight(value, "lb") ?? ""));
    setUnit(u);
  };
  const field =
    unit === "lb" ? (
      <NumberField
        id="bench-weight"
        label="Weight"
        value={lbText}
        onChange={(v) => {
          setLbText(v);
          onChange(v === "" ? "" : String(fromDisplayWeight(v, "lb") ?? ""));
        }}
        step={5}
        unit="lb"
        placeholder="0"
      />
    ) : (
      <NumberField id="bench-weight" label="Weight" value={value} onChange={onChange} step={2.5} unit="kg" placeholder="0" />
    );
  return (
    <div className="grid gap-1.5">
      {field}
      <div className="flex justify-end">
        <UnitToggle value={unit} onChange={switchTo} />
      </div>
    </div>
  );
};

const Numbers = ({ form, set }) => {
  if (form.type === "lift") {
    const isVbtLift = VBT_LIFT_KEYS.includes(normaliseExerciseKey(form.label));
    return (
      <>
        <WeightField value={form.valueNumber} onChange={(v) => set({ valueNumber: v })} />
        <NumberField id="bench-reps" label="Reps" value={form.reps} onChange={(v) => set({ reps: v })} step={1} min={1} placeholder="0" />
        <p className="text-xs leading-snug text-muted-foreground">
          1 rep means a max you tested.
          {isVbtLift && " On this lift your 1RM also sets the Coach's rep timing."}
        </p>
      </>
    );
  }
  if (form.type === "distance") {
    return (
      <>
        <NumberField id="bench-distance" label="Distance" value={form.forAmount} onChange={(v) => set({ forAmount: v, forUnit: "m" })} step={100} unit="m" placeholder="0" />
        <ClockField id="bench-time" label="Time" value={form.valueClock} onChange={(v) => set({ valueClock: v })} />
      </>
    );
  }
  if (form.type === "calories") {
    return (
      <>
        <NumberField id="bench-cals" label="Calories" value={form.valueNumber} onChange={(v) => set({ valueNumber: v })} step={1} unit="cal" placeholder="0" />
        <ClockField id="bench-time" label="Time" value={form.inClock} onChange={(v) => set({ inClock: v })} />
      </>
    );
  }
  const isTime = form.unit === "sec";
  return (
    <>
      {isTime ? (
        <ClockField id="bench-value" label="Result" value={form.valueClock} onChange={(v) => set({ valueClock: v })} />
      ) : (
        <NumberField
          id="bench-value"
          label="Result"
          value={form.valueNumber}
          onChange={(v) => set({ valueNumber: v })}
          step={1}
          unit={OTHER_UNITS.find((u) => u.value === form.unit)?.label}
          placeholder="0"
        />
      )}
      <ChipGrid
        options={OTHER_UNITS}
        value={form.unit}
        onChange={(unit) => set({ unit, valueNumber: "", valueClock: "" })}
      />
    </>
  );
};

const Effort = ({ form, set }) => {
  const options = effortOptions(form.type);
  const picked = options.find((o) => o.value === form.effort);
  const lift = form.type === "lift";
  return (
    <Field label={lift ? "Reps left in the tank" : "How much you had left"}>
      <div
        role="group"
        aria-label={lift ? "Reps left in the tank" : "How much you had left"}
        className={cn(
          "grid gap-1 rounded-xl border border-border bg-background p-1",
          lift ? "grid-cols-5" : "grid-cols-3",
        )}
      >
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            aria-pressed={form.effort === o.value}
            onClick={() => set({ effort: form.effort === o.value ? "" : o.value })}
            className={cn(
              "rounded-lg px-1 py-2 text-sm text-muted-foreground transition-colors",
              form.effort === o.value && "bg-card font-semibold text-foreground ring-1 ring-border",
            )}
          >
            {o.label}
          </button>
        ))}
      </div>
      <p className="text-xs leading-snug text-muted-foreground">
        {picked
          ? picked.hint
          : form.effort
            ? `Saved as "${form.effort}". Pick one to replace it.`
            : lift
              ? "How many more reps you could have done. It decides how firm the 1RM is."
              : "It decides how much the result counts towards your threshold."}
      </p>
    </Field>
  );
};

const BenchmarkEntryForm = ({ form, set, setType, isNew, saved, error, confirming, onAskDelete, onCancelDelete, onDelete, busy }) => {
  const [more, setMore] = useState(Boolean(form.notes || form.secPerRep));
  const age = benchmarkAge({ tested_on: form.testedOn || null, created_at: saved?.created_at });
  const moreSummary = [form.notes && "notes", form.secPerRep && `${form.secPerRep} s/rep`].filter(Boolean).join(" · ");

  return (
    <>
      <Field label="Exercise or test" htmlFor="bench-label">
        <Input
          id="bench-label"
          value={form.label}
          onChange={(e) => set({ label: e.target.value })}
          placeholder="e.g. Back Squat, Rower 2K, Pull-ups"
        />
      </Field>
      <Field label="What you did">
        <div role="group" aria-label="What you did" className="grid grid-cols-2 gap-2">
          {RESULT_TYPES.map((t) => (
            <button
              key={t.value}
              type="button"
              aria-pressed={form.type === t.value}
              onClick={() => setType(t.value)}
              className={cn(
                "grid gap-0.5 rounded-xl border border-border bg-background px-3 py-2.5 text-left transition-colors",
                form.type === t.value && "border-primary bg-primary/10",
              )}
            >
              <span className="text-sm font-semibold text-foreground">{t.label}</span>
              <span className="text-xs tabular-nums text-muted-foreground">{t.example}</span>
            </button>
          ))}
        </div>
      </Field>
      <div className="grid gap-3 rounded-2xl border border-border bg-muted/20 p-3">
        <Numbers form={form} set={set} />
        <Estimate form={form} />
      </div>
      <Effort form={form} set={set} />
      <div className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-card">
        <label
          htmlFor="bench-date"
          className="mb-0 grid grid-cols-[auto_minmax(0,1fr)] items-center gap-3 px-3 py-1.5 text-sm font-medium text-foreground"
        >
          When
          <input
            id="bench-date"
            type="date"
            max={todayIso()}
            value={form.testedOn}
            onChange={(e) => set({ testedOn: e.target.value })}
            className="h-9 min-w-0 justify-self-end bg-transparent text-right text-sm font-normal text-muted-foreground outline-none [color-scheme:dark]"
          />
        </label>
        <button
          type="button"
          aria-expanded={more}
          onClick={() => setMore((m) => !m)}
          className="grid w-full grid-cols-[auto_minmax(0,1fr)_1rem] items-center gap-3 px-3 py-3 text-left text-sm font-medium text-foreground hover:bg-muted/30"
        >
          Notes and more
          <span className="truncate text-right text-[13px] font-normal text-muted-foreground">{moreSummary || "optional"}</span>
          {more ? <ChevronDown className="h-4 w-4 text-muted-foreground/70" /> : <ChevronRight className="h-4 w-4 text-muted-foreground/70" />}
        </button>
      </div>
      {(!form.testedOn && age) || age?.stale ? (
        <p className="-mt-2 text-xs leading-snug text-muted-foreground">
          {!form.testedOn && age
            ? `No date. Added ${age.date.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}${age.stale ? ", so it may be out of date" : ""}.`
            : "Over six months ago, so it may be out of date. Retest when you can."}
        </p>
      ) : null}
      {more && (
        <>
          <Field label="Notes" htmlFor="bench-notes">
            <Input
              id="bench-notes"
              value={form.notes}
              onChange={(e) => set({ notes: e.target.value })}
              placeholder="e.g. Damper 5, paused reps"
            />
          </Field>
          {(form.type === "lift" || form.type === "other") && (
            <Field label="Seconds per rep" htmlFor="bench-tempo" aside="sets the Coach's timing">
              <Input
                id="bench-tempo"
                type="number"
                inputMode="decimal"
                min="0"
                step="0.1"
                value={form.secPerRep}
                onChange={(e) => set({ secPerRep: e.target.value })}
                placeholder="Worked out for you"
              />
            </Field>
          )}
          <Field label="Kind" aside="picked from what you did">
            <ChipGrid options={BENCHMARK_KINDS} value={form.category} onChange={(category) => set({ category })} allowNone />
          </Field>
        </>
      )}
      {error && <p className="text-sm text-destructive">{error}</p>}
      {!isNew && (
        <DeleteConfirm
          name={form.label || "this benchmark"}
          consequence="The AI and the Coach's timing stop using it."
          confirming={confirming}
          onAsk={onAskDelete}
          onCancel={onCancelDelete}
          onConfirm={onDelete}
          busy={busy}
        />
      )}
    </>
  );
};

export default BenchmarkEntryForm;
